// Export-time gap fill from prod_data.
//
// A property's survey_source flag picks the authoritative tree (legacy `data`
// vs prod_data), but some legacy trees are missing answers that normalization
// already produced into prod_data (final_survey_answer). Exports should ship
// the best available answer, so after resolving through the gate, leaves with
// an EMPTY value are filled from the matching prod_data entry — never
// overwriting an existing answer. Missing evidence lists are filled the same
// way (they feed the Summary photo slots). Join: leaf_id === leaf ref_id/id,
// falling back to the cleaned question title (ambiguous titles match nothing).

import type { AttributeField } from "./types.ts";
import { isNotApplicable } from "./gcEvaluationFill.ts";

/**
 * Strips the [section] suffix from a question title and lowercases it —
 * same normalization as useTemplateOptions.cleanTitle, duplicated here so
 * this module stays free of the supabase/firebase import chain (testable).
 */
function cleanTitle(title: string): string {
  return title.replace(/\s*\[.*?\]\s*$/, "").trim().toLowerCase();
}

interface ProdEntry {
  final_survey_answer?: unknown;
  /** ENG-498 answer-status contract — see resolveSurveyAttributes.ts. */
  answer_status?: string | null;
  human_answer?: unknown;
  ai_answer?: unknown;
  leaf_id?: string | null;
  question_title?: string | null;
  evidence?: unknown;
}

const isEmpty = (v: unknown) =>
  v === null ||
  v === undefined ||
  v === "" ||
  isNotApplicable(v) ||
  (Array.isArray(v) && v.length === 0);

/** prod_data evidence is `[{url,...}]` or string[]; leaves want string[]. */
function normalizeEvidence(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => (typeof x === "string" ? x : (x as { url?: string } | null)?.url))
    .filter((u): u is string => typeof u === "string" && u.length > 0);
}

export function fillAnswerGapsFromProdData(
  fields: AttributeField[],
  prodData: Record<string, unknown> | null | undefined
): AttributeField[] {
  if (!prodData || typeof prodData !== "object") return fields;

  const byLeafId = new Map<string, ProdEntry>();
  const byTitle = new Map<string, ProdEntry>();
  const ambiguous = new Set<string>();
  for (const raw of Object.values(prodData)) {
    const entry = raw as ProdEntry;
    if (!entry || typeof entry !== "object") continue;
    if (entry.leaf_id) byLeafId.set(entry.leaf_id, entry);
    const title = entry.question_title ? cleanTitle(entry.question_title) : "";
    if (!title) continue;
    if (ambiguous.has(title)) continue;
    if (byTitle.has(title)) {
      byTitle.delete(title);
      ambiguous.add(title);
    } else {
      byTitle.set(title, entry);
    }
  }
  if (byLeafId.size === 0 && byTitle.size === 0) return fields;

  const fill = (list: AttributeField[]): AttributeField[] =>
    list.map((field) => {
      if (field.type === "node") {
        return { ...field, fields: fill(field.fields) } as AttributeField;
      }
      const leaf = field as AttributeField & {
        value?: unknown;
        evidence?: unknown;
        ref_id?: string;
      };
      const entry =
        byLeafId.get(leaf.ref_id ?? leaf.id ?? "") ??
        byTitle.get(cleanTitle(leaf.title ?? ""));
      if (!entry) return field;

      let out = leaf;
      // A reviewer's not_applicable/unknown decision must win over a stale
      // human/AI candidate value from before that decision — same reasoning
      // as resolveSurveyAttributes.ts's makeLeaf(); both statuses get the same
      // "N/A" sentinel there (either way nobody has a usable answer). isEmpty()
      // itself treats "N/A" as empty (that's how it stays a no-op downstream
      // when the template's own format column rejects it), so a decided status
      // needs its own bypass of the "is there a usable answer" gate.
      const isDecidedNoAnswer =
        entry.answer_status === "not_applicable" || entry.answer_status === "unknown";
      const answer = isDecidedNoAnswer
        ? "N/A"
        : [entry.final_survey_answer, entry.human_answer, entry.ai_answer].find(
            (v) => !isEmpty(v)
          );
      if (isEmpty(leaf.value) && (isDecidedNoAnswer || !isEmpty(answer))) {
        let value: unknown = answer;
        if (leaf.type === "multiSelect" || leaf.type === "photos") {
          value = Array.isArray(value) ? value : [value];
        }
        out = { ...out, value } as typeof out;
      }
      const evidence = normalizeEvidence(entry.evidence);
      if (evidence.length > 0 && isEmpty(leaf.evidence)) {
        out = { ...out, evidence };
      }
      return out as AttributeField;
    });

  return fill(fields);
}
